import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Search, Mic, X, FlaskConical, Filter, ArrowUpDown, 
  Clock, Plus, Check, ShoppingBag, ChevronRight, 
  Layers, ShieldCheck, HeartPulse, Sparkles, Building2,
  Trash2, Flame, Tag, CheckCircle2
} from "lucide-react";
import axios from "axios";
import { getAllTests } from "../../services/LabTestApi/PopularTest";
import { fetchSymptoms } from "../../services/LabTestApi/symptomsApi";
import { useCartActions } from "../../hooks/useCartActions";
import { useLocationContext } from "../../context/LocationContext";

export default function LabTestPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { address } = useLocationContext();

  const cart = useSelector((state) => state.cart.items || []);
  const { addWithReplaceCheck, removeItem, LoginPopupComponent } = useCartActions();

  const [tests, setTests] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSymptomId, setSelectedSymptomId] = useState("all");
  const [sortBy, setSortBy] = useState("popularity");
  const [selectedForCompare, setSelectedForCompare] = useState([]);

  // Load All Tests & Symptoms
  useEffect(() => {
    let isMounted = true;
    const loadAllTestData = async () => {
      setLoading(true);
      try {
        // 1. Fetch Symptoms for filter pills
        const sympData = await fetchSymptoms();
        if (isMounted && Array.isArray(sympData)) {
          setSymptoms(sympData);
        }

        // 2. Fetch Master Tests Catalog (500+ items)
        const testRes = await getAllTests(1, 500);
        const rawList = Array.isArray(testRes?.data) ? testRes.data : (Array.isArray(testRes) ? testRes : []);

        const normalized = rawList.map(t => {
          const sPrice = Number(t.SellingPrice ?? t.sellingPrice ?? t.price ?? 0);
          const tPrice = Number(t.TestPrice ?? t.testPrice ?? t.price ?? sPrice);
          const disc = Number(t.Discount ?? t.discount ?? 0);
          return {
            ...t,
            testID: t.TestID || t.testID || t.testId || t.id,
            testCode: t.TestCode || t.testCode || "",
            testName: t.TestName || t.testName || t.name || "Diagnostic Blood Test",
            testPrice: tPrice > 0 ? tPrice : Math.round(sPrice * 1.4),
            sellingPrice: sPrice > 0 ? sPrice : tPrice,
            discount: disc,
            testImage: t.TestImage || t.testImage || "",
            labId: Number(t.LabId || t.labId || t.storeId || 1),
            labName: t.LabName || t.labName || "Certified Partner Lab",
            symptomId: Number(t.SymptomId || t.symptomId || t.symptomsId || 0),
            description: t.Description || t.description || "",
            reportTime: t.ReportTime || t.reportTime || 24,
            isActive: t.IsActive !== false && t.isActive !== false,
          };
        });

        // Deduplicate tests by testID
        const seen = new Set();
        const uniqueTests = [];
        for (const t of normalized) {
          if (!t.testName || t.isActive === false) continue;
          if (seen.has(t.testID)) continue;
          seen.add(t.testID);
          uniqueTests.push(t);
        }

        if (isMounted) {
          setTests(uniqueTests);
        }
      } catch (err) {
        console.error("Error fetching lab tests:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadAllTestData();
    return () => { isMounted = false; };
  }, []);

  const isTestInCart = (testId) => {
    return cart.some((item) => Number(item.productId) === Number(testId) && Number(item.categoryId) === 2);
  };

  const handleTestCartToggle = async (test) => {
    const testId = test.testID;
    if (isTestInCart(testId)) {
      removeItem(testId, 2);
    } else {
      await addWithReplaceCheck({
        productId: testId,
        name: test.testName,
        price: test.sellingPrice || test.testPrice || 0,
        discountedPrice: test.discount || 0,
        categoryId: 2,
        storeId: test.labId || 1,
        image: test.testImage || "",
      });
    }
  };

  const toggleCompare = (testId) => {
    setSelectedForCompare((prev) => 
      prev.includes(testId) 
        ? prev.filter((id) => id !== testId) 
        : prev.length < 4 ? [...prev, testId] : prev
    );
  };

  const getTestImgUrl = (test) => {
    const img = test.testImage;
    if (!img || img === "null" || img === "undefined") return null;
    const clean = img.toString().trim();
    if (clean.startsWith("http")) return clean;
    if (clean.startsWith("TestMasterImage/") || clean.startsWith("TestImage/") || clean.startsWith("LabImage/")) {
      return `https://admin.medrphalabs.com/${clean}`;
    }
    return `https://admin.medrphalabs.com/TestImage/${clean}`;
  };

  // Filter & Sort
  const filteredAndSortedTests = useMemo(() => {
    let result = tests.filter((t) => {
      const nameMatch = (t.testName || "").toLowerCase().includes(searchQuery.toLowerCase());
      if (!nameMatch) return false;

      if (selectedSymptomId === "all") return true;
      return Number(t.symptomId) === Number(selectedSymptomId);
    });

    if (sortBy === "price_asc") {
      result.sort((a, b) => (a.sellingPrice || 0) - (b.sellingPrice || 0));
    } else if (sortBy === "price_desc") {
      result.sort((a, b) => (b.sellingPrice || 0) - (a.sellingPrice || 0));
    } else if (sortBy === "discount") {
      result.sort((a, b) => (b.discount || 0) - (a.discount || 0));
    }

    return result;
  }, [tests, searchQuery, selectedSymptomId, sortBy]);

  const totalCartPrice = cart.reduce((sum, item) => sum + (Number(item.price) || 0) * (Number(item.qty) || 1), 0);

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-32 text-slate-800 font-sans">
      {LoginPopupComponent}

      {/* ================= 1. FLUTTER SIGNATURE RED HEADER ================= */}
      <div className="bg-gradient-to-r from-red-600 via-rose-600 to-red-700 text-white py-6 px-4 sm:px-6 lg:px-10 shadow-md">
        <div className="max-w-[1720px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            
            <div>
              <div className="flex items-center gap-2">
                <span className="bg-white/20 text-white text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1">
                  <FlaskConical size={11} className="text-amber-300" />
                  <span>NABL Certified Diagnostic Tests</span>
                </span>
                <span className="text-red-100 text-xs font-semibold">
                  {tests.length} Total Tests Available
                </span>
              </div>

              <h1 className="text-2xl sm:text-3xl font-black mt-1 tracking-tight text-white">
                Diagnostic Lab Tests & Profiles
              </h1>
              
              <p className="text-xs sm:text-sm text-red-100 mt-1 flex items-center gap-1.5 font-medium">
                <ShieldCheck size={14} className="text-emerald-300 shrink-0" />
                <span>100% Accurate Reports • Free Home Blood Sample Collection</span>
              </p>
            </div>

            {/* Header Search Box */}
            <div className="w-full md:w-96">
              <div className="h-11 bg-white rounded-2xl px-4 flex items-center justify-between shadow-md text-slate-800">
                <div className="flex items-center gap-2.5 text-slate-400 w-full">
                  <Search size={18} className="text-red-600 shrink-0" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search CBC, Thyroid, HbA1c, Vitamin..."
                    className="w-full bg-transparent focus:outline-none text-xs sm:text-sm text-slate-800 placeholder-slate-400 font-medium"
                  />
                </div>
                {searchQuery ? (
                  <button onClick={() => setSearchQuery("")} className="text-slate-400 p-1 hover:text-slate-600">
                    <X size={16} />
                  </button>
                ) : (
                  <Mic size={17} className="text-red-600" />
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      <div className="w-full max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-10 mt-6 space-y-6">
        
        {/* ================= 2. HEALTH CONCERNS / SYMPTOMS FILTER PILLS ================= */}
        <div className="bg-white border border-slate-200/90 rounded-3xl p-5 shadow-2xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3.5">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-5 bg-gradient-to-b from-red-500 to-rose-600 rounded-full" />
              <h3 className="text-sm sm:text-base font-extrabold text-slate-900 leading-tight">
                Filter by Health Concern
              </h3>
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2 self-end sm:self-auto">
              <span className="text-xs font-bold text-slate-500 flex items-center gap-1">
                <ArrowUpDown size={13} />
                <span>Sort by:</span>
              </span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-slate-50 border border-slate-200 text-slate-800 text-xs font-bold rounded-xl px-3 py-1.5 focus:outline-none focus:border-red-500"
              >
                <option value="popularity">Popularity</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="discount">Biggest Discount</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2.5 overflow-x-auto no-scrollbar py-1">
            <button
              onClick={() => setSelectedSymptomId("all")}
              className={`px-4 py-2 rounded-2xl border text-xs sm:text-sm font-bold transition-all shrink-0 ${
                selectedSymptomId === "all"
                  ? "bg-red-600 text-white border-red-600 shadow-md shadow-red-200 scale-102"
                  : "bg-slate-50 hover:bg-red-50 text-slate-700 hover:text-red-600 border-slate-200"
              }`}
            >
              All Diagnostic Tests ({tests.length})
            </button>

            {symptoms.map((s, idx) => {
              const sId = s.id || idx;
              const sName = s.name || s.symptomName || "Condition";
              const isActive = Number(sId) === Number(selectedSymptomId);

              return (
                <button
                  key={sId}
                  onClick={() => setSelectedSymptomId(sId)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-2xl border text-xs sm:text-sm font-bold transition-all shrink-0 ${
                    isActive
                      ? "bg-red-600 text-white border-red-600 shadow-md shadow-red-200 scale-102"
                      : "bg-slate-50 hover:bg-red-50 text-slate-700 hover:text-red-600 border-slate-200"
                  }`}
                >
                  <HeartPulse size={14} className={isActive ? "text-white" : "text-red-600"} />
                  <span>{sName}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ================= 3. TESTS GRID (Exact Flutter TestCardNew Layout) ================= */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-4 border border-[#E8F0FE] animate-pulse flex items-center gap-3.5">
                <div className="w-[68px] h-[68px] bg-slate-200 rounded-xl shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-slate-200 rounded w-1/2" />
                  <div className="h-3 bg-slate-100 rounded w-1/3" />
                  <div className="h-4 bg-slate-100 rounded w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredAndSortedTests.length === 0 ? (
          <div className="bg-white rounded-3xl p-16 text-center border border-slate-200 max-w-md mx-auto my-10 shadow-sm">
            <FlaskConical size={48} className="text-slate-300 mx-auto mb-3" />
            <h3 className="text-base font-bold text-slate-800">No Diagnostic Tests Found</h3>
            <p className="text-xs text-slate-500 mt-1">No tests matching "{searchQuery}"</p>
            <button
              onClick={() => { setSearchQuery(""); setSelectedSymptomId("all"); }}
              className="mt-4 bg-red-600 text-white font-bold text-xs px-5 py-2.5 rounded-xl hover:bg-red-700 shadow-xs"
            >
              Reset Filters
            </button>
          </div>
        ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
              {filteredAndSortedTests.map((test, idx) => {
                const testId = test.testID;
                const inCart = isTestInCart(testId);
                const testTitle = test.testName || "Diagnostic Blood Test";
                const sellingPrice = test.sellingPrice || 299;
                const originalPrice = test.testPrice || Math.round(sellingPrice * 1.4);
                const discount = originalPrice > sellingPrice ? Math.round(((originalPrice - sellingPrice) / originalPrice) * 100) : 30;
                const testImgUrl = getTestImgUrl(test);
                const isCompared = selectedForCompare.includes(testId);

                return (
                  <div
                    key={`${testId}-${idx}`}
                    onClick={() => navigate(`/TestDetail/${testId}`)}
                    className="bg-white rounded-2xl border border-slate-200/90 hover:border-red-400 p-3.5 shadow-2xs hover:shadow-lg transition-all flex flex-col justify-between cursor-pointer group relative overflow-hidden"
                  >
                    <div>
                      {/* Large Top Image Showcase (Matches Packages Card) */}
                      <div className="h-36 sm:h-40 w-full rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center p-2.5 relative overflow-hidden">
                        
                        {/* Discount Badge */}
                        <div className="absolute top-2 left-2 flex items-center gap-1 bg-[#E53935] text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded shadow-2xs z-10">
                          <span>{discount}% OFF</span>
                        </div>

                        {/* Compare Toggle Checkbox */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCompare(testId);
                          }}
                          className={`absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded transition-all border flex items-center gap-1 z-10 ${
                            isCompared 
                              ? "bg-indigo-600 text-white border-indigo-600 shadow-xs" 
                              : "bg-white/90 backdrop-blur-xs text-slate-700 border-slate-200 hover:border-indigo-300"
                          }`}
                          title="Compare Test"
                        >
                          <Layers size={10} />
                          <span>{isCompared ? "Added" : "Compare"}</span>
                        </button>

                        {/* Fast Report Badge */}
                        <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-white/95 backdrop-blur-xs text-blue-800 text-[10px] font-extrabold px-2 py-0.5 rounded shadow-xs border border-blue-100 z-10">
                          <Clock size={11} className="text-blue-600" />
                          <span>24 hrs Report</span>
                        </div>

                        {/* Live Image or Name Banner Fallback */}
                        {testImgUrl ? (
                          <img 
                            src={testImgUrl} 
                            alt={testTitle} 
                            className="max-h-full max-w-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = e.target.parentElement.querySelector('.name-fallback');
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                        ) : null}

                        {/* Product Name Fallback when image missing */}
                        <div 
                          className={`name-fallback w-full h-full flex-col items-center justify-center text-center p-3 bg-gradient-to-br from-red-50 to-rose-100/60 rounded-lg ${testImgUrl ? 'hidden' : 'flex'}`}
                        >
                          <div className="w-12 h-12 rounded-full bg-[#E53935] text-white flex items-center justify-center mb-2 shadow-md">
                            <FlaskConical size={22} />
                          </div>
                          <span className="text-xs font-black text-slate-800 line-clamp-2 px-1 leading-tight text-center">
                            {testTitle}
                          </span>
                        </div>
                      </div>

                      {/* Test Information */}
                      <div className="mt-3">
                        <div className="inline-flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.2 rounded mb-1 truncate max-w-full">
                          <Building2 size={10} className="shrink-0" />
                          <span className="truncate">{test.labName || "Certified Lab"}</span>
                        </div>

                        <h4 className="text-xs sm:text-sm font-extrabold text-slate-900 group-hover:text-[#E53935] transition-colors line-clamp-2 leading-snug">
                          {testTitle}
                        </h4>

                        <div className="mt-2 flex items-baseline gap-2">
                          <span className="text-sm font-black text-slate-900">₹{sellingPrice}</span>
                          <span className="text-xs text-slate-400 line-through">₹{originalPrice}</span>
                        </div>
                      </div>
                    </div>

                    {/* Bottom Cart Action Button */}
                    <div className="mt-3 pt-2.5 border-t border-slate-100">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTestCartToggle(test);
                        }}
                        className={`w-full py-2 px-3 rounded-xl text-xs font-bold transition-all border flex items-center justify-center gap-1.5 shadow-2xs ${
                          inCart 
                            ? "bg-[#E53935] hover:bg-red-700 text-white border-[#E53935] shadow-sm" 
                            : "bg-white text-[#E53935] border-[#E53935] hover:bg-red-50"
                        }`}
                      >
                        {inCart ? (
                          <>
                            <Trash2 size={13} />
                            <span>REMOVE</span>
                          </>
                        ) : (
                          <>
                            <Plus size={13} />
                            <span>ADD TO CART</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
        )}

      </div>

      {/* ================= 4. FLUTTER COMPARE FLOATING BAR ================= */}
      {selectedForCompare.length >= 2 && (
        <div className="fixed bottom-20 left-4 right-4 max-w-md mx-auto z-40 bg-[#1E1B4B] text-white p-3.5 rounded-2xl shadow-2xl flex items-center justify-between gap-3 border border-indigo-500/30">
          <div className="flex items-center gap-2">
            <div className="flex -space-x-2 overflow-hidden">
              {selectedForCompare.map((id, i) => (
                <div key={id} className="w-7 h-7 rounded-full bg-indigo-500 border-2 border-[#1E1B4B] flex items-center justify-center text-[10px] font-bold">
                  {i + 1}
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs font-bold text-white leading-tight">
                {selectedForCompare.length} Tests Selected
              </p>
              <p className="text-[10px] text-indigo-200">Ready to compare</p>
            </div>
          </div>

          <button
            onClick={() => navigate("/test-comparison", { state: { testIds: selectedForCompare } })}
            className="bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white font-bold text-xs px-4 py-2 rounded-xl shadow-md flex items-center gap-1"
          >
            <span>Compare Now</span>
            <ChevronRight size={13} />
          </button>
        </div>
      )}

      {/* ================= 5. STICKY BOTTOM CART BAR ================= */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 p-3 sm:p-4 shadow-2xl">
          <div className="max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 text-[#E53935] flex items-center justify-center font-black">
                <ShoppingBag size={20} />
              </div>
              <div>
                <span className="text-xs font-semibold text-slate-500">
                  {cart.length} {cart.length === 1 ? "Item" : "Items"} in Cart
                </span>
                <p className="text-base sm:text-lg font-black text-slate-900">
                  Total: ₹{totalCartPrice}
                </p>
              </div>
            </div>

            <button
              onClick={() => navigate("/cart")}
              className="bg-[#E53935] hover:bg-red-700 text-white font-black text-xs sm:text-sm px-6 py-2.5 rounded-xl shadow-md transition-all flex items-center gap-1.5"
            >
              <span>View Cart & Checkout</span>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

    </div>
  );
}